import {
  Body2,
  Button,
  mergeClasses,
  Persona,
  Skeleton,
  SkeletonItem,
  Spinner,
  Textarea,
  Text,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowDownload20Regular,
  ArrowMaximize20Regular,
  ArrowMinimize20Regular,
  ArrowRight16Regular,
  ArrowUpload20Regular,
  CheckmarkCircle20Regular,
  Clipboard20Regular,
  ClipboardCheckmark20Regular,
  DocumentAdd20Regular,
  DocumentFolder20Regular,
  Sparkle20Filled,
} from "@fluentui/react-icons";

import {
  addMessageToConversation,
  createConversation,
  exportDiagnosticDocx,
  getAIResponse,
  getCategories,
  getMessagesOfConversation,
  getWorkspaceLibraryDocuments,
} from "api";
import {
  addProjectIqWorkspaceDocument,
  getDeliveryMethodConfig,
  patchDiagnosticChatConversation,
} from "apps/project-iq/api";
import { IDeliveryMethodConfig } from "apps/project-iq/models/IDeliveryMethodConfig";
import { getChunkAndUpdateAIMessage } from "common/streamProcessing.js";
import { ManageContent } from "components/chat/manageContent/ManageContent";
import { IWorkspaceLibraryDocument } from "components/manageWorkspaces/workspaceLibrary/workspaceLibrary.types";
import { ICategory } from "models/ICategory";
import { ICitation } from "models/ICitation";
import { IFile } from "models/IFile";
import { ITag } from "models/ITag";
import { IMessage } from "models/IMessage";
import { IStandardChartData } from "models/IChartData";
import { IAIResponseError } from "models/IResponseError";
import { IUser } from "models/IUser";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "react-query";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "store";
import { safeDecodeURIComponent, uploadFilesToStorage } from "utils/blobUtils";
import { v4 as uuidv4 } from "uuid";
import { ConstantValues } from "common/constants";
import { usePatchDiagnosticStatus } from "apps/project-iq/hooks/usePatchDiagnosticStatus";
import { saveDiagnosticChat } from "redux/diagnosticChatSlice";
import {
  copyRichText,
  copyTextToClipboard,
  stripMarkdown,
} from "utils/generalUtils";
import { resolveAssistantLlmType } from "utils/llmModelMigration";
import { useStyles } from "./DiagnosticChat.styles";
import { IDiagnosticChatProps } from "./DiagnosticChat.types";
import { ILLMConfig } from "redux/llmConfig";

const PAGE_KEY = "project-iq-chat";
const AIMS_URL_RE = /https?:\/\/aims(-stage)?\.northhighland\.ai[^\s"')<>]*/g;

function extractWorkspaceChatParams(
  urlString: string,
): { promptId: string; useCaseId: string | null } | null {
  try {
    const url = new URL(urlString);
    const promptId =
      url.searchParams.get("promptId") ?? url.searchParams.get("promptid");
    if (!promptId) return null;
    const useCaseId = url.searchParams.get("useCaseId");
    return { promptId, useCaseId };
  } catch {
    const promptIdMatch = /[?&]promptId=([^&\s"')<>]+)/i.exec(urlString);
    if (!promptIdMatch) return null;
    const useCaseIdMatch = /[?&]useCaseId=([^&\s"')<>]+)/i.exec(urlString);
    return {
      promptId: promptIdMatch[1],
      useCaseId: useCaseIdMatch?.[1] ?? null,
    };
  }
}

function replaceAimsUrls(content: string, workspaceId: string): string {
  if (!content.includes("northhighland.ai")) return content;
  return content.replace(AIMS_URL_RE, (match) => {
    const extracted = extractWorkspaceChatParams(match);
    if (!extracted) return match;
    const params = new URLSearchParams({ promptId: extracted.promptId });
    if (extracted.useCaseId) params.set("useCaseId", extracted.useCaseId);
    return `${window.location.origin}/workspace/${workspaceId}/chat?${params}`;
  });
}

const CHAT_LABELS: Record<
  string,
  {
    conversationPrefix: string;
    sessionLabel: string;
    welcomeTitle: string;
    welcomeSubtitle: string;
    assistantName: string;
  }
> = {
  workshops_focus_groups: {
    conversationPrefix: "Workshop",
    sessionLabel: "Workshop Session",
    welcomeTitle: "Plan your workshop session",
    welcomeSubtitle:
      "I can help you structure the workshop agenda, craft discussion questions, and organize breakout sessions.",
    assistantName: "Workshop Assistant",
  },
  in_person_interviews: {
    conversationPrefix: "In-Person Interview",
    sessionLabel: "Interview Session",
    welcomeTitle: "Plan your interview session",
    welcomeSubtitle:
      "I can help you structure your interview guide, craft questions, and organize your session flow.",
    assistantName: "Interview Assistant",
  },
};

const DEFAULT_CHAT_LABELS = {
  conversationPrefix: "Diagnostic",
  sessionLabel: "Diagnostic Session",
  welcomeTitle: "Plan your diagnostic session",
  welcomeSubtitle: "I can help you plan and run this diagnostic session.",
  assistantName: "Diagnostic Assistant",
};

const VALID_ASSISTANT_PERSONA_NAMES = new Set([
  ...Object.values(CHAT_LABELS).map((labels) => labels.assistantName),
  DEFAULT_CHAT_LABELS.assistantName,
]);

export const DiagnosticChat = ({
  workspaceId,
  diagnosticId,
  diagnosticName,
  deliveryMethod,
  chatConversationId: initialChatConversationId,
}: IDiagnosticChatProps): JSX.Element => {
  const chatLabels = CHAT_LABELS[deliveryMethod] ?? DEFAULT_CHAT_LABELS;
  const styles = useStyles();
  const dispatch = useDispatch();
  const store = useStore<RootState>();

  const currentUser: IUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );

  const availableModels = useSelector(
    (state: RootState) => state.accessLLMModelsSlice.value,
  );
  const storedLLMConfig = useSelector(
    (state: RootState) => state.currentLLMConfigSlice.config[PAGE_KEY],
  );
  // Sonnet 5.5 only when the user can see it; re-evaluated as models load.
  const currentLLMConfig = useMemo(
    () =>
      storedLLMConfig ?? {
        llmType: resolveAssistantLlmType(availableModels),
        rag: false,
        citate: false,
        sourceRagCategories: [],
      },
    [storedLLMConfig, availableModels],
  );

  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [text, setText] = useState<string>(ConstantValues.EMPTY_STRING);
  const [aiGenerateMessage, setAiGenerateMessage] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [isManageContentOpen, setIsManageContentOpen] = useState(false);
  const [allKnowledgeComponents, setAllKnowledgeComponents] = useState<ITag[]>(
    [],
  );
  const [selectedKnowledgeComponents, setSelectedKnowledgeComponents] =
    useState<ITag[]>([]);
  const [allWorkspaceDocuments, setAllWorkspaceDocuments] = useState<
    IWorkspaceLibraryDocument[]
  >([]);
  const [selectedWorkspaceDocuments, setSelectedWorkspaceDocuments] = useState<
    IWorkspaceLibraryDocument[]
  >([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<IFile[]>([]);
  const [isLoadingKnowledgeComponents, setIsLoadingKnowledgeComponents] =
    useState(false);
  const [isLoadingWorkspaceDocuments, setIsLoadingWorkspaceDocuments] =
    useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isLoadError, setIsLoadError] = useState(false);
  const [isConfigError, setIsConfigError] = useState(false);
  const [isKnowledgeComponentsError, setIsKnowledgeComponentsError] =
    useState(false);
  const [isWorkspaceDocumentsError, setIsWorkspaceDocumentsError] =
    useState(false);
  const [isUploadError, setIsUploadError] = useState(false);

  const [isExpanded, setIsExpanded] = useState(false);
  const [clipboardCopiedIndex, setClipboardCopiedIndex] = useState<
    number | null
  >(null);
  const [downloadedIndex, setDownloadedIndex] = useState<number | null>(null);
  const [addedToContextIndex, setAddedToContextIndex] = useState<number | null>(
    null,
  );
  const [downloadingIndex, setDownloadingIndex] = useState<number | null>(null);
  const [addingToContextIndex, setAddingToContextIndex] = useState<
    number | null
  >(null);
  const [downloadErrorIndex, setDownloadErrorIndex] = useState<number | null>(
    null,
  );
  const [addToContextErrorIndex, setAddToContextErrorIndex] = useState<
    number | null
  >(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const pendingScrollRef = useRef(false);
  const messageContentRefs = useRef<Record<number, HTMLElement | null>>({});
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const expandButtonRef = useRef<HTMLButtonElement>(null);
  const conversationIdRef = useRef<string | null>(conversationId);
  const diagnosticIdRef = useRef<string>(diagnosticId);
  const diagnosticIdAtSendRef = useRef<string>(diagnosticId);
  const justLoadedConversationRef = useRef<boolean>(false);
  const [deliveryMethodConfig, setDeliveryMethodConfig] =
    useState<IDeliveryMethodConfig | null>(null);
  const navigate = useNavigate();
  useEffect(() => {
    conversationIdRef.current = conversationId;
  });
  useEffect(() => {
    diagnosticIdRef.current = diagnosticId;
  });

  useEffect(() => {
    let resolvedConversationId: string | null =
      initialChatConversationId ?? null;

    if (!resolvedConversationId) {
      const reduxSaved = store.getState().diagnosticChatSlice?.[diagnosticId];
      if (reduxSaved?.conversationId) {
        resolvedConversationId = reduxSaved.conversationId;
      }
    }

    if (
      resolvedConversationId &&
      resolvedConversationId === conversationIdRef.current
    ) {
      return;
    }

    if (resolvedConversationId) {
      setConversationId(resolvedConversationId);
      pendingScrollRef.current = true;
      setText(ConstantValues.EMPTY_STRING);
      setIsLoadingMessages(true);
      messageContentRefs.current = {};
      justLoadedConversationRef.current = true;
      getMessagesOfConversation({ conversationId: resolvedConversationId })
        .then((msgs: IMessage[]) => {
          if (resolvedConversationId !== conversationIdRef.current) return;
          setMessages(
            (msgs ?? []).map((msg) =>
              msg.role !== "assistant"
                ? msg
                : {
                    ...msg,
                    content: replaceAimsUrls(msg.content, workspaceId),
                  },
            ),
          );
        })
        .catch(() => {
          setIsLoadError(true);
          justLoadedConversationRef.current = false;
        })
        .finally(() => setIsLoadingMessages(false));
    } else {
      justLoadedConversationRef.current = true;
      setMessages([]);
      setConversationId(null);
      setIsLoadError(false);
      messageContentRefs.current = {};
      setText(ConstantValues.EMPTY_STRING);
      setAiGenerateMessage(ConstantValues.EMPTY_STRING);
      setSelectedKnowledgeComponents([]);
      setSelectedWorkspaceDocuments([]);
      setUploadedDocuments([]);
    }
  }, [diagnosticId, diagnosticName, initialChatConversationId, store]);

  useEffect(() => {
    if (conversationId) {
      const entry = { conversationId };
      dispatch(saveDiagnosticChat({ diagnosticId, entry }));
    }
  }, [conversationId, diagnosticId, dispatch]);

  useEffect(() => {
    if (pendingScrollRef.current && messagesContainerRef.current) {
      pendingScrollRef.current = false;
      const behavior = justLoadedConversationRef.current ? "auto" : "smooth";
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior,
      });
      justLoadedConversationRef.current = false;
    }
  }, [messages]);

  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = "0";
      ta.style.height = `${ta.scrollHeight}px`;
    }
  }, [text]);

  useEffect(() => {
    const fetchDeliveryMethodConfig = async () => {
      try {
        const config = await getDeliveryMethodConfig(deliveryMethod);
        setDeliveryMethodConfig(config);
      } catch {
        setIsConfigError(true);
      }
    };
    fetchDeliveryMethodConfig();
  }, []);

  useEffect(() => {
    const fetchKnowledgeComponents = async () => {
      setIsLoadingKnowledgeComponents(true);
      try {
        const categories = await getCategories();
        const allTags: ITag[] = [];
        categories?.forEach((category: ICategory) => {
          if (category.tags) {
            category.tags.forEach((tag: ITag) => {
              allTags.push({ ...tag, category });
            });
          }
        });

        const filteredTags = allTags.filter((tag) => tag.isPublic === true);
        setAllKnowledgeComponents(filteredTags);
      } catch {
        setIsKnowledgeComponentsError(true);
      } finally {
        setIsLoadingKnowledgeComponents(false);
      }
    };
    fetchKnowledgeComponents();
  }, []);

  useEffect(() => {
    if (
      allKnowledgeComponents.length > 0 &&
      selectedKnowledgeComponents.length === 0 &&
      deliveryMethodConfig
    ) {
      const selectedKcs = allKnowledgeComponents.filter(
        (tag) =>
          tag.id !== undefined &&
          deliveryMethodConfig.knowledgeComponentIds.includes(tag.id),
      );
      if (selectedKcs.length > 0) {
        setSelectedKnowledgeComponents(selectedKcs);
      }

      if (deliveryMethodConfig.documents.length > 0) {
        const configFiles: IFile[] = deliveryMethodConfig.documents.map(
          (doc) => ({
            id: doc.filename,
            filename: doc.filename,
            url: doc.url ?? "",
          }),
        );
        setUploadedDocuments((prev) => {
          const existingIds = new Set(prev.map((f) => f.id));
          return [
            ...prev,
            ...configFiles.filter((f) => !existingIds.has(f.id)),
          ];
        });
      }
    }
  }, [
    allKnowledgeComponents,
    deliveryMethodConfig,
    selectedKnowledgeComponents.length,
  ]);

  const fetchWorkspaceDocuments = useCallback(async () => {
    if (!workspaceId) return;
    setIsLoadingWorkspaceDocuments(true);
    try {
      const docs = await getWorkspaceLibraryDocuments({ workspaceId });
      setAllWorkspaceDocuments(docs || []);
    } catch {
      setIsWorkspaceDocumentsError(true);
    } finally {
      setIsLoadingWorkspaceDocuments(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchWorkspaceDocuments();
  }, [fetchWorkspaceDocuments]);

  const contentSourceCount = useMemo(
    () =>
      selectedKnowledgeComponents.length +
      selectedWorkspaceDocuments.length +
      uploadedDocuments.length,
    [
      selectedKnowledgeComponents,
      selectedWorkspaceDocuments,
      uploadedDocuments,
    ],
  );

  const buildWorkspaceChatHref = useCallback(
    (urlString: string): string | null => {
      const extracted = extractWorkspaceChatParams(urlString);
      if (!extracted) return null;
      const params = new URLSearchParams({ promptId: extracted.promptId });
      if (extracted.useCaseId) params.set("useCaseId", extracted.useCaseId);
      return `/workspace/${workspaceId}/chat?${params}`;
    },
    [workspaceId],
  );

  const markdownComponents = useMemo(
    () => ({
      a: ({
        href,
        children,
        ...props
      }: React.AnchorHTMLAttributes<HTMLAnchorElement> & {
        node?: unknown;
      }) => {
        if (href) {
          const internalHref = buildWorkspaceChatHref(href);
          if (internalHref) {
            const navPath = internalHref.startsWith("http")
              ? new URL(internalHref).pathname + new URL(internalHref).search
              : internalHref;
            return (
              <a
                href={navPath}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(navPath);
                }}
                {...props}
              >
                {children}
              </a>
            );
          }
          if (href.startsWith("http")) {
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                {...props}
              >
                {children}
              </a>
            );
          }
        }
        return (
          <a href={href} {...props}>
            {children}
          </a>
        );
      },
    }),
    [workspaceId, navigate, buildWorkspaceChatHref],
  );

  const transformAimsLinks = useCallback(
    (content: string): string => replaceAimsUrls(content, workspaceId),
    [workspaceId],
  );

  const buildLlmConfig = (): ILLMConfig => ({
    ...currentLLMConfig,
    rag:
      selectedKnowledgeComponents.length > 0 ||
      selectedWorkspaceDocuments.length > 0,
    citate: false,
    sourceRagCategories: [],
  });

  const buildMessage = ({
    content,
    role,
    chartData,
    sourceDocuments,
    files,
    citations,
  }: {
    content: string;
    role: string;
    chartData?: IStandardChartData;
    sourceDocuments?: unknown[];
    files: IFile[];
    citations: ICitation[];
  }): IMessage => ({
    id: uuidv4(),
    content,
    citations,
    files,
    isHidden: false,
    chartData,
    role,
    tags: selectedKnowledgeComponents,
    sourceDocuments,
    llmConfig: buildLlmConfig(),
    createdAt: Math.floor(Date.now() / 1000),
    displayName:
      role === "assistant"
        ? chatLabels.assistantName
        : currentUser?.displayName,
  });

  const { mutate: patchStatus } = usePatchDiagnosticStatus(diagnosticId);

  const chatWithAi = async () => {
    if (!text || !text.trim()) {
      setText(ConstantValues.EMPTY_STRING);
      return;
    }

    const diagnosticIdAtSend = diagnosticId;
    diagnosticIdAtSendRef.current = diagnosticIdAtSend;

    const optimisticUserMessage = buildMessage({
      content: text,
      role: "user",
      files: [],
      citations: [],
    });

    const newMessages = [...messages, optimisticUserMessage];
    pendingScrollRef.current = true;
    setMessages(newMessages);
    setText(ConstantValues.EMPTY_STRING);
    setIsStreaming(true);
    setAiGenerateMessage(ConstantValues.EMPTY_STRING);

    let activeConversationId = conversationId;

    if (!activeConversationId) {
      const created = await createConversation({
        conversation: {
          title: `${chatLabels.conversationPrefix}: ${diagnosticName}`,
          pinned: false,
          messages: [],
          workspaceId,
          isDiagnosticChat: true,
        },
      });
      activeConversationId = created.id;
      if (diagnosticIdRef.current !== diagnosticIdAtSend) return;
      setConversationId(created.id);
      patchDiagnosticChatConversation({
        diagnosticId,
        chatConversationId: created.id,
      }).catch(() => {});
      patchStatus({ status: "in_progress" });
    }

    const llmConfig = buildLlmConfig();

    const persistedUserMessage = await addMessageToConversation({
      conversationId: activeConversationId,
      message: {
        content: text,
        files: [],
        isHidden: false,
        role: "user",
        tags: selectedKnowledgeComponents,
        llmConfig,
        displayName: currentUser?.displayName,
      },
    });

    if (diagnosticIdRef.current !== diagnosticIdAtSend) return;

    setMessages((prev) =>
      prev.map((m) =>
        m.id === optimisticUserMessage.id ? persistedUserMessage : m,
      ),
    );

    const messagesForAi = newMessages.map((m) =>
      m.id === optimisticUserMessage.id ? persistedUserMessage : m,
    );

    const response = await (
      getAIResponse as (...args: unknown[]) => Promise<unknown>
    )({
      llmConfig,
      messages: messagesForAi,
      persistFiles: false,
      workspaceId,
      deliveryMethodConfigMethod: deliveryMethodConfig?.method ?? null,
    });

    if ("error" in (response as Record<string, unknown>)) {
      if (diagnosticIdRef.current !== diagnosticIdAtSend) return;
      setIsStreaming(false);
      throw new Error(
        `AI Response Error: ${(response as any).status || "Unknown"} - ${(response as any).message || "Something went wrong"}`,
      );
    }

    const { content, chartData, sourceDocuments, citations } =
      await getChunkAndUpdateAIMessage(
        response,
        (response as Response).body,
        null,
        setAiGenerateMessage as any,
        null,
        dispatch,
      );

    if (!content) {
      if (diagnosticIdRef.current !== diagnosticIdAtSend) return;
      setIsStreaming(false);
      setAiGenerateMessage(ConstantValues.EMPTY_STRING);
      return;
    }

    const optimisticAiMessage = buildMessage({
      content: replaceAimsUrls(content, workspaceId),
      role: "assistant",
      chartData,
      sourceDocuments,
      files: [],
      citations: citations ?? [],
    });

    if (diagnosticIdRef.current !== diagnosticIdAtSend) return;

    setMessages((prev) => [...prev, optimisticAiMessage]);
    setIsStreaming(false);
    setAiGenerateMessage(ConstantValues.EMPTY_STRING);

    const persistedAiMessage = await addMessageToConversation({
      conversationId: activeConversationId,
      message: {
        content,
        files: [],
        isHidden: false,
        chartData,
        role: "assistant",
        tags: selectedKnowledgeComponents,
        sourceDocuments,
        citations: citations ?? [],
        llmConfig,
        displayName: chatLabels.assistantName,
      },
    });

    if (diagnosticIdRef.current !== diagnosticIdAtSend) return;

    setMessages((prev) =>
      prev.map((m) =>
        m.id === optimisticAiMessage.id ? persistedAiMessage : m,
      ),
    );

    return response;
  };

  const chatMutation = useMutation({
    mutationFn: chatWithAi,
    onSuccess: () => {
      setAiGenerateMessage(ConstantValues.EMPTY_STRING);
      setText(ConstantValues.EMPTY_STRING);
      setIsStreaming(false);
    },
    onError: (error: IAIResponseError) => {
      setIsStreaming(false);
      setAiGenerateMessage(ConstantValues.EMPTY_STRING);
      if (diagnosticIdRef.current !== diagnosticIdAtSendRef.current) return;
      const errorMessage =
        error?.message || "Something went wrong. Please try again later.";
      const errorContent = `**Error:** ${errorMessage}`;
      const errorMsg = buildMessage({
        content: errorContent,
        role: "assistant",
        files: [],
        citations: [],
      });
      setMessages((prev) => [...prev, errorMsg]);
    },
  });

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !workspaceId) return;
      setIsUploadError(false);
      try {
        const uploadedFiles = await uploadFilesToStorage(
          [{ id: Date.now().toString(), file }],
          "workspace_library",
          workspaceId,
          false,
        );
        if (uploadedFiles.length > 0) {
          setUploadedDocuments((prev) => [...prev, uploadedFiles[0]]);
        }
      } catch {
        setIsUploadError(true);
      } finally {
        event.target.value = "";
      }
    },
    [workspaceId],
  );

  const handleCopyToClipboard = async (content: string, index: number) => {
    const transformedContent = transformAimsLinks(content);
    try {
      const messageElement = messageContentRefs.current[index];
      if (messageElement) {
        await copyRichText(messageElement, stripMarkdown(transformedContent));
      } else {
        await copyTextToClipboard(stripMarkdown(transformedContent));
      }
      setClipboardCopiedIndex(index);
      setTimeout(() => setClipboardCopiedIndex(null), 2000);
    } catch {
      setClipboardCopiedIndex(null);
    }
  };

  const handleDownloadAsWord = async (
    messageId: string | undefined,
    index: number,
  ) => {
    if (!conversationId || !messageId) {
      setDownloadErrorIndex(index);
      return;
    }
    setDownloadErrorIndex(null);
    setDownloadingIndex(index);
    try {
      const blob = await exportDiagnosticDocx({
        diagnosticId,
        conversationId,
        messageId,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${diagnosticName}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setDownloadedIndex(index);
      setTimeout(() => setDownloadedIndex(null), 2000);
    } catch {
      setDownloadErrorIndex(index);
    } finally {
      setDownloadingIndex(null);
    }
  };

  const handleAddToProjectContext = async (
    messageId: string | undefined,
    index: number,
  ) => {
    if (!workspaceId || !conversationId || !messageId) {
      setAddToContextErrorIndex(index);
      return;
    }
    setAddToContextErrorIndex(null);
    setAddingToContextIndex(index);
    try {
      const blob = await exportDiagnosticDocx({
        diagnosticId,
        conversationId,
        messageId,
      });
      const displayFilename = `${diagnosticName}.docx`;
      const file = new File([blob], displayFilename, { type: blob.type });
      const uploadedFiles = await uploadFilesToStorage(
        [{ id: Date.now().toString(), file }],
        "workspace_library",
        workspaceId,
        false,
      );
      if (uploadedFiles.length > 0) {
        const blobFilename = safeDecodeURIComponent(
          uploadedFiles[0].url?.split("/").pop() || displayFilename,
        );
        await addProjectIqWorkspaceDocument({
          workspaceId,
          filename: blobFilename,
          originalFilename: displayFilename,
          source: "file",
        });
      }
      setAddedToContextIndex(index);
      setTimeout(() => setAddedToContextIndex(null), 2000);
    } catch {
      setAddToContextErrorIndex(index);
    } finally {
      setAddingToContextIndex(null);
    }
  };

  const onSend = () => chatMutation.mutate();

  const onEnterPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (chatMutation.isLoading) return;
      chatMutation.mutate();
    }
  };

  const hasMessages = messages.length > 0;
  const isSendDisabled = !text || text.trim() === "";

  const configErrorNotice = isConfigError ? (
    <div role="alert" className={styles.configErrorBanner}>
      Could not load session configuration. The assistant will respond without a
      pre-configured prompt or knowledge components.
    </div>
  ) : null;

  const knowledgeComponentsErrorNotice = isKnowledgeComponentsError ? (
    <div role="alert" className={styles.configErrorBanner}>
      Could not load knowledge components.
    </div>
  ) : null;

  const workspaceDocumentsErrorNotice = isWorkspaceDocumentsError ? (
    <div role="alert" className={styles.configErrorBanner}>
      Could not load workspace documents.
    </div>
  ) : null;

  const uploadErrorNotice = isUploadError ? (
    <div role="alert" className={styles.configErrorBanner}>
      Could not upload the document. Please check the file type and try again.
    </div>
  ) : null;

  const composer = (
    <div className={styles.inputCard}>
      <Textarea
        id="diagnostic-chat-textarea"
        placeholder={`Ask the ${chatLabels.assistantName} to plan the session for ${diagnosticName} — add agenda details, attendees, or goals...`}
        resize="none"
        ref={textareaRef}
        aria-label="Chat message"
        textarea={{
          className: mergeClasses(
            styles.innerTextarea,
            chatMutation.isLoading && styles.innerTextareaBusy,
          ),
        }}
        className={styles.textArea}
        value={text}
        onChange={(_e, data) => setText(data.value)}
        readOnly={chatMutation.isLoading}
        aria-disabled={chatMutation.isLoading || undefined}
        onKeyDown={onEnterPress}
      />

      <div className={styles.toolbarRow}>
        <div className={styles.toolbarActions}>
          <Tooltip content="Content Sources" relationship="description">
            <div className={styles.contentSourceButtonWrapper}>
              <Button
                className={styles.contentSourceButton}
                icon={<DocumentFolder20Regular aria-hidden="true" />}
                onClick={() => setIsManageContentOpen(true)}
                aria-label={`Manage content sources${contentSourceCount > 0 ? `, ${contentSourceCount} selected` : ""}`}
              />
              {contentSourceCount > 0 && (
                <span className={styles.contentSourceBadge} aria-hidden="true">
                  {contentSourceCount}
                </span>
              )}
            </div>
          </Tooltip>
          <input
            type="file"
            ref={fileInputRef}
            accept=".pdf,.docx,.doc,.txt,.pptx,.xlsx,.csv"
            style={{ display: "none" }}
            onChange={handleFileUpload}
          />
          <Tooltip content="Upload Document" relationship="description">
            <Button
              className={styles.uploadButton}
              icon={<ArrowUpload20Regular aria-hidden="true" />}
              onClick={() => fileInputRef.current?.click()}
              aria-label="Upload document"
            />
          </Tooltip>
        </div>
        <button
          disabled={isSendDisabled}
          onClick={onSend}
          className={mergeClasses(
            styles.sendButton,
            isSendDisabled && styles.sendButtonDisabled,
          )}
          aria-label="Send message"
          title="Send message"
        >
          <ArrowRight16Regular aria-hidden="true" />
        </button>
      </div>
    </div>
  );

  return (
    <div
      className={mergeClasses(styles.root, isExpanded && styles.expandedRoot)}
    >
      <div className={styles.chatHeader}>
        {isExpanded && (
          <Text className={styles.chatHeaderTitle}>NH Diagnostic IQ</Text>
        )}
        <Tooltip
          content={isExpanded ? "Collapse" : "Expand chat"}
          relationship="description"
        >
          <Button
            ref={expandButtonRef}
            appearance="subtle"
            className={styles.expandButton}
            icon={
              isExpanded ? (
                <ArrowMinimize20Regular aria-hidden="true" />
              ) : (
                <ArrowMaximize20Regular aria-hidden="true" />
              )
            }
            onClick={() => {
              const collapsing = isExpanded;
              setIsExpanded((prev) => !prev);
              if (collapsing) {
                requestAnimationFrame(() => expandButtonRef.current?.focus());
              }
            }}
            aria-label={
              isExpanded ? "Collapse chat" : "Expand chat to full screen"
            }
          />
        </Tooltip>
      </div>
      {isLoadError ? (
        <div role="alert" className={styles.loadErrorBanner}>
          Failed to load conversation history. Please refresh the page.
        </div>
      ) : isLoadingMessages ? (
        <div className={styles.messagesArea}>
          <div className={styles.messagesContainer}>
            {[80, 60, 90, 50].map((width, i) => (
              <div
                key={i}
                className={styles.messageRow}
                style={
                  {
                    "--skeleton-width": `${width}%`,
                    "--skeleton-width-narrow": `${width - 20}%`,
                  } as React.CSSProperties
                }
              >
                <Skeleton aria-label="Loading messages">
                  <div className={styles.skeletonContent}>
                    <SkeletonItem
                      animation="wave"
                      size={8}
                      className={styles.skeletonItemHeader}
                    />
                    <SkeletonItem
                      animation="wave"
                      size={8}
                      className={styles.skeletonItemWide}
                    />
                    <SkeletonItem
                      animation="wave"
                      size={8}
                      className={styles.skeletonItemNarrow}
                    />
                  </div>
                </Skeleton>
              </div>
            ))}
          </div>
        </div>
      ) : !hasMessages && !isStreaming ? (
        <>
          <div className={styles.welcomeArea}>
            <div className={styles.welcomeBadge}>
              <Sparkle20Filled
                className={styles.welcomeBadgeIcon}
                aria-hidden="true"
              />
              <Text>
                {chatLabels.sessionLabel} · {diagnosticName}
              </Text>
            </div>
            <Text className={styles.welcomeTitle}>
              {chatLabels.welcomeTitle}
            </Text>
            <Text className={styles.welcomeSubtitle}>
              {chatLabels.welcomeSubtitle}
            </Text>
          </div>

          <div className={styles.messageBarArea}>
            {configErrorNotice}
            {knowledgeComponentsErrorNotice}
            {workspaceDocumentsErrorNotice}
            {uploadErrorNotice}
            {composer}
          </div>
        </>
      ) : (
        <>
          <div ref={messagesContainerRef} className={styles.messagesArea}>
            <div className={styles.messagesContainer}>
              {messages.map((message, index) => {
                const assistantDisplayName =
                  message.displayName &&
                  VALID_ASSISTANT_PERSONA_NAMES.has(message.displayName)
                    ? message.displayName
                    : chatLabels.assistantName;
                return (
                  <div
                    key={index}
                    className={mergeClasses(
                      styles.messageRow,
                      message.role !== "assistant" && styles.userMessage,
                    )}
                  >
                    <Persona
                      name={
                        message.role === "assistant"
                          ? assistantDisplayName
                          : (message.displayName ?? currentUser?.displayName)
                      }
                      secondaryText={message.role}
                      size="large"
                      avatar={
                        message.role === "assistant"
                          ? { image: { src: "/images/NH_Logo_Circle.png" } }
                          : {
                              name:
                                message.displayName ?? currentUser?.displayName,
                            }
                      }
                    />
                    <div
                      ref={(el) => {
                        messageContentRefs.current[index] = el;
                      }}
                    >
                      <Body2 className={styles.messageContent}>
                        <Markdown
                          remarkPlugins={[remarkGfm]}
                          components={markdownComponents}
                        >
                          {transformAimsLinks(message.content)}
                        </Markdown>
                      </Body2>
                    </div>
                    {message.role === "assistant" && (
                      <div className={styles.messageActions}>
                        <Tooltip
                          content="Copy to Clipboard"
                          relationship="label"
                        >
                          <button
                            className={styles.messageActionButton}
                            onClick={() =>
                              handleCopyToClipboard(message.content, index)
                            }
                            aria-label="Copy to Clipboard"
                          >
                            {clipboardCopiedIndex === index ? (
                              <ClipboardCheckmark20Regular aria-hidden="true" />
                            ) : (
                              <Clipboard20Regular aria-hidden="true" />
                            )}
                          </button>
                        </Tooltip>
                        <Tooltip
                          content="Download as Word"
                          relationship="label"
                        >
                          <button
                            className={styles.messageActionButton}
                            onClick={() =>
                              handleDownloadAsWord(message.id, index)
                            }
                            disabled={
                              downloadingIndex === index ||
                              chatMutation.isLoading
                            }
                            aria-label="Download as Word document"
                          >
                            {downloadingIndex === index ? (
                              <Spinner size="tiny" aria-hidden="true" />
                            ) : downloadedIndex === index ? (
                              <CheckmarkCircle20Regular aria-hidden="true" />
                            ) : (
                              <ArrowDownload20Regular aria-hidden="true" />
                            )}
                          </button>
                        </Tooltip>
                        <Tooltip
                          content="Add to project context"
                          relationship="label"
                        >
                          <button
                            className={styles.messageActionButton}
                            onClick={() =>
                              handleAddToProjectContext(message.id, index)
                            }
                            disabled={
                              addingToContextIndex === index ||
                              chatMutation.isLoading
                            }
                            aria-label="Add to project context"
                          >
                            {addingToContextIndex === index ? (
                              <Spinner size="tiny" aria-hidden="true" />
                            ) : addedToContextIndex === index ? (
                              <CheckmarkCircle20Regular aria-hidden="true" />
                            ) : (
                              <DocumentAdd20Regular aria-hidden="true" />
                            )}
                          </button>
                        </Tooltip>
                        {downloadErrorIndex === index && (
                          <div
                            role="alert"
                            className={styles.messageActionError}
                          >
                            Could not download the document. Please try again.
                          </div>
                        )}
                        {addToContextErrorIndex === index && (
                          <div
                            role="alert"
                            className={styles.messageActionError}
                          >
                            Could not add the document to project context.
                            Please try again.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {isStreaming && (
                <div className={styles.messageRow}>
                  <Persona
                    name={chatLabels.assistantName}
                    secondaryText="assistant"
                    size="large"
                    avatar={{ image: { src: "/images/NH_Logo_Circle.png" } }}
                  />
                  {aiGenerateMessage && aiGenerateMessage.length > 0 ? (
                    <Body2 className={styles.messageContent}>
                      <Markdown
                        remarkPlugins={[remarkGfm]}
                        components={markdownComponents}
                      >
                        {transformAimsLinks(aiGenerateMessage)}
                      </Markdown>
                    </Body2>
                  ) : (
                    <Body2 className={styles.messageContent}>
                      <Skeleton aria-label="Loading response">
                        <div className={styles.skeletonContent}>
                          <SkeletonItem
                            animation="wave"
                            size={8}
                            className={styles.skeletonItem80}
                          />
                          <SkeletonItem
                            animation="wave"
                            size={8}
                            className={styles.skeletonItem60}
                          />
                          <SkeletonItem
                            animation="wave"
                            size={8}
                            className={styles.skeletonItem20}
                          />
                        </div>
                      </Skeleton>
                    </Body2>
                  )}
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className={styles.messageBarArea}>
            {configErrorNotice}
            {knowledgeComponentsErrorNotice}
            {workspaceDocumentsErrorNotice}
            {uploadErrorNotice}
            {composer}
          </div>
        </>
      )}

      <ManageContent
        isOpen={isManageContentOpen}
        isPersonalWorkspace={false}
        onClose={() => setIsManageContentOpen(false)}
        knowledgeComponents={allKnowledgeComponents}
        selectedKnowledgeComponents={selectedKnowledgeComponents}
        onKnowledgeComponentsChange={setSelectedKnowledgeComponents}
        workspaceDocuments={allWorkspaceDocuments}
        selectedWorkspaceDocuments={selectedWorkspaceDocuments}
        onWorkspaceDocumentsChange={setSelectedWorkspaceDocuments}
        uploadedDocuments={uploadedDocuments}
        onUploadedDocumentsChange={setUploadedDocuments}
        availableConversations={[]}
        selectedConversations={[]}
        onConversationsChange={() => {}}
        workspaceId={workspaceId}
        onWorkspaceDocumentsRefresh={fetchWorkspaceDocuments}
        isLoadingKnowledgeComponents={isLoadingKnowledgeComponents}
        isLoadingWorkspaceDocuments={isLoadingWorkspaceDocuments}
        isLoadingConversations={false}
      />
    </div>
  );
};
